import {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
    useContext,
} from "react"
import SsnInput from "../SsnInput"
import NationalIdInput from "../NationaIdentification"

import ApplicantDataFormContext from '../../Contexts/ApplicantDataFormContext'

const SsnOrIdentityInput = forwardRef(({ title = "", data = {}, readOnly = false }, ref) => {
    const [doNotHaveUsSsn, setDoNotHaveUsSsn] = useState(data.doNotHaveUsSsn || false)

    const [error, setError] = useState("")

    const ssnRef = useRef()
    const confirmSsnRef = useRef()
    const nationalIdentityNumberRef = useRef()
    const confirmNationalIdentityNumberRef = useRef()

    const applicantDataForm = useContext(ApplicantDataFormContext)

    const ssnLastFourDigit = applicantDataForm?.current?.["ssnLastFourDigit"]

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const ssnResult = doNotHaveUsSsn ? null : ssnRef.current?.validateAndGetData()
            const confirmSsnResult = doNotHaveUsSsn ? null : confirmSsnRef.current?.validateAndGetData()
            const nationalIdResult = doNotHaveUsSsn ? nationalIdentityNumberRef.current?.validateAndGetData() : null
            const confirmNationalIdResult = doNotHaveUsSsn ? confirmNationalIdentityNumberRef.current?.validateAndGetData() : null

            const value = { doNotHaveUsSsn }
            let isValid = true
            let mismatchError = false

            if (doNotHaveUsSsn) {
                value.nationalIdentityNumber = nationalIdResult?.value
                value.confirmNationalIdentityNumber = confirmNationalIdResult?.value

                const valuesMatch = nationalIdResult?.value === confirmNationalIdResult?.value
                isValid = (nationalIdResult?.isValid ?? true) &&
                    (confirmNationalIdResult?.isValid ?? true) &&
                    valuesMatch
                mismatchError = !valuesMatch
                if (isValid && ssnLastFourDigit && nationalIdResult.value.slice(-4) !== ssnLastFourDigit) {
                    isValid = false
                    setError("The National Id you entered doesn't match the last 4 digits you provided earlier. Please double-check and try again.")
                }
            } else {
                value.ssn = ssnResult?.value
                value.confirmSsn = confirmSsnResult?.value

                const valuesMatch = ssnResult?.value === confirmSsnResult?.value
                isValid = (ssnResult?.isValid ?? true) &&
                    (confirmSsnResult?.isValid ?? true) &&
                    valuesMatch
                mismatchError = !valuesMatch

                if (isValid && ssnLastFourDigit && ssnResult.value.slice(-4) !== ssnLastFourDigit) {
                    isValid = false
                    setError("The SSN you entered doesn't match the last 4 digits you provided earlier. Please double-check and try again.")
                }

            }

            if (doNotHaveUsSsn) {
                confirmNationalIdentityNumberRef.current?.setError(
                    mismatchError,
                    mismatchError ? 'National ID Numbers do not match' : ''
                )
            } else {
                confirmSsnRef.current?.setError(
                    mismatchError,
                    mismatchError ? 'SSNs do not match' : ''
                )
            }
            if (isValid) {
                setError("")
            }

            return { isValid, value }
        }
    }))

    return (
        <div className="my-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1 space-y-4">
                    {!doNotHaveUsSsn && (
                        <>
                            <SsnInput
                                ref={ssnRef}
                                data={data?.ssn ?? ""}
                                readOnly={readOnly}
                                title="SSN*"
                            />
                            <SsnInput
                                ref={confirmSsnRef}
                                data={data?.confirmSsn ?? ""}
                                readOnly={readOnly}
                                title="Confirm SSN*"
                            />
                        </>
                    )}
                    {doNotHaveUsSsn && (
                        <>
                            <NationalIdInput
                                ref={nationalIdentityNumberRef}
                                data={data?.nationalIdentityNumber ?? ""}
                                disabled={readOnly}
                                title="National Identification Number"
                                isMandatory={true}
                                type="password"
                                showToggle={true}
                            />
                            <NationalIdInput
                                ref={confirmNationalIdentityNumberRef}
                                data={data?.confirmNationalIdentityNumber ?? ""}
                                disabled={readOnly}
                                title="Confirm National Identification Number"
                                isMandatory={true}
                                type="password"
                                showToggle={true}
                            />
                        </>
                    )}
                </div>
                <div className="flex items-start sm:items-center space-x-2 sm:w-auto">
                    <input
                        type="checkbox"
                        checked={doNotHaveUsSsn}
                        disabled={readOnly}
                        onChange={() => setDoNotHaveUsSsn(!doNotHaveUsSsn)}
                        className="form-checkbox h-4 w-4 text-blue-600 mt-1 sm:mt-0"
                    />
                    <span
                        onClick={() => !readOnly && setDoNotHaveUsSsn(!doNotHaveUsSsn)}
                        className={`cursor-pointer text-sm sm:text-base ${doNotHaveUsSsn ? "text-blue-600 font-medium" : "text-gray-700"}`}
                    >
                        I do not have a US Social Security Number
                    </span>
                </div>
            </div>

            {error && (
                <p className="text-red-600 text-sm mt-2">
                    {error}
                </p>
            )}

        </div>
    )
})

export default SsnOrIdentityInput
